import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  CheckCircle2, XCircle, Clock, Eye, Building2, FileText,
  Download, User, Mail, Phone, MapPin, Globe, Image as ImageIcon, AlertTriangle,
} from 'lucide-react';

const statusConfig = {
  pending: { label: 'Pending', icon: Clock, color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Approved', icon: CheckCircle2, color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'bg-red-100 text-red-800' },
};

const docLabels: Record<string, string> = {
  trade_license_url: 'Business License',
  trade_name_registration_url: 'Trade Registration',
  tin_certificate_url: 'TIN Certificate',
  vat_certificate_url: 'VAT Certificate',
  logo_url: 'Agency Logo',
  business_registration_url: 'Business Registration',
  tax_clearance_url: 'Tax Clearance',
  company_profile_url: 'Company Profile',
  id_document_url: 'Manager ID',
  photo_url: 'Manager Photo',
};

export function AgencyApplications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedApp, setSelectedApp] = useState<any>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [duplicateConfirm, setDuplicateConfirm] = useState<{ existingId: string; name: string } | null>(null);

  const { data: applications, isLoading } = useQuery({
    queryKey: ['agency-applications-dedicated'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('agency_applications' as any)
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });

  const { data: profiles } = useQuery({
    queryKey: ['agency-app-profiles', applications?.map((a: any) => a.user_id)],
    queryFn: async () => {
      if (!applications?.length) return {};
      const userIds = applications.map((a: any) => a.user_id);
      const { data } = await supabase.from('profiles').select('*').in('user_id', userIds);
      return Object.fromEntries((data || []).map((p: any) => [p.user_id, p]));
    },
    enabled: !!applications?.length,
  });

  const performApproval = async (app: any, notes: string, force = false) => {
    // Duplicate-name guard
    if (!force) {
      const { data: existing } = await supabase
        .from('agencies')
        .select('id, name')
        .ilike('name', app.agency_name)
        .maybeSingle();
      if (existing) {
        setDuplicateConfirm({ existingId: existing.id, name: existing.name });
        return { duplicate: true };
      }
    }

    const { error: updateError } = await supabase
      .from('agency_applications' as any)
      .update({ status: 'approved', reviewer_notes: notes, reviewed_by: user!.id, reviewed_at: new Date().toISOString() })
      .eq('id', app.id);
    if (updateError) throw updateError;

    const { error: agErr } = await supabase.from('agencies').insert({
      name: app.agency_name,
      description: app.description ? { en: app.description, am: '', om: '' } : null,
      phone: app.phone, email: app.email, city: app.city, address: app.address, website: app.website,
      logo_url: app.logo_url || null,
      is_verified: true, created_by: app.user_id,
      manager_name: app.manager_name || null,
      whatsapp: app.whatsapp || null,
      established_year: app.established_year || null,
      employee_count: app.employee_count || null,
      services_offered: app.services_offered || [],
      specializations: app.specializations || [],
      areas_of_operation: app.areas_of_operation || [],
      social_facebook: app.social_facebook || null,
      social_instagram: app.social_instagram || null,
      social_linkedin: app.social_linkedin || null,
      social_telegram: app.social_telegram || null,
    } as any);
    if (agErr && !agErr.message.includes('duplicate')) throw agErr;

    const { error: roleErr } = await supabase.from('user_roles').insert({ user_id: app.user_id, role: 'agency_admin' as any });
    if (roleErr && !roleErr.message.includes('duplicate')) throw roleErr;

    // Notify applicant
    await supabase.from('notifications').insert({
      user_id: app.user_id,
      title: 'Your agency application was approved',
      message: notes ? `Reviewer notes: ${notes}` : 'Welcome aboard! You can now manage your agency.',
      type: 'application_approved',
      metadata: { application_id: app.id, application_type: 'agency' },
    });
    return { duplicate: false };
  };

  const reviewMutation = useMutation({
    mutationFn: async ({ appId, status, notes, force }: { appId: string; status: string; notes: string; force?: boolean }) => {
      const app = applications?.find((a: any) => a.id === appId);
      if (!app) throw new Error('Application not found');

      if (status === 'approved') {
        return await performApproval(app, notes, force);
      }
      // Reject
      const { error } = await supabase
        .from('agency_applications' as any)
        .update({ status: 'rejected', reviewer_notes: notes, reviewed_by: user!.id, reviewed_at: new Date().toISOString() })
        .eq('id', appId);
      if (error) throw error;

      await supabase.from('notifications').insert({
        user_id: app.user_id,
        title: 'Your agency application was rejected',
        message: notes ? `Reviewer notes: ${notes}` : 'Please review and resubmit with corrections.',
        type: 'application_rejected',
        metadata: { application_id: app.id, application_type: 'agency' },
      });
      return { duplicate: false };
    },
    onSuccess: (result) => {
      if (result?.duplicate) return;
      queryClient.invalidateQueries({ queryKey: ['agency-applications-dedicated'] });
      toast.success(actionType === 'approve' ? 'Agency approved! Profile created and applicant notified.' : 'Application rejected. Applicant notified.');
      setSelectedApp(null);
      setActionType(null);
      setReviewNotes('');
      setDuplicateConfirm(null);
    },
    onError: (err: any) => toast.error(err.message),
  });

  const getDocUrl = async (path: string) => {
    const { data } = await supabase.storage.from('developer-documents').createSignedUrl(path, 3600);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
  };

  const viewDocImage = async (path: string) => {
    const { data } = await supabase.storage.from('developer-documents').createSignedUrl(path, 3600);
    if (data?.signedUrl) setPreviewImage(data.signedUrl);
  };

  const pendingCount = applications?.filter((a: any) => a.status === 'pending').length || 0;

  const renderInfoRow = (icon: React.ReactNode, label: string, value: string | number | null | undefined) => {
    if (!value) return null;
    return (
      <div className="flex items-start gap-3 py-2">
        <div className="text-muted-foreground mt-0.5">{icon}</div>
        <div>
          <div className="text-xs text-muted-foreground font-medium">{label}</div>
          <div className="text-sm font-medium">{value}</div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20" />)}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-bold">Agency Applications</h2>
        {pendingCount > 0 && <Badge className="bg-yellow-500 text-white">{pendingCount} Pending</Badge>}
      </div>

      {!applications?.length ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No agency applications yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {applications.map((app: any) => {
            const cfg = statusConfig[app.status as keyof typeof statusConfig] || statusConfig.pending;
            const profile = profiles?.[app.user_id];
            return (
              <Card key={app.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
                      {app.logo_url ? (
                        <Building2 className="h-6 w-6 text-primary" />
                      ) : <Building2 className="h-6 w-6 text-primary" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{app.agency_name}</div>
                      <div className="text-sm text-muted-foreground truncate">
                        {profile?.full_name || app.manager_name || 'Unknown'} • {app.city || 'N/A'} • {new Date(app.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <Badge className={cfg.color}><cfg.icon className="h-3 w-3 mr-1" /> {cfg.label}</Badge>
                    <Button variant="outline" size="sm" onClick={() => { setSelectedApp(app); setReviewNotes(''); }}>
                      <Eye className="h-4 w-4 mr-1" /> Review
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!selectedApp} onOpenChange={() => { setSelectedApp(null); setActionType(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Agency Application: {selectedApp?.agency_name}
            </DialogTitle>
            {selectedApp && (
              <DialogDescription className="flex items-center gap-2 mt-1">
                <Badge className={statusConfig[selectedApp.status as keyof typeof statusConfig]?.color || ''}>
                  {statusConfig[selectedApp.status as keyof typeof statusConfig]?.label || selectedApp.status}
                </Badge>
                <span className="text-xs">
                  Submitted {new Date(selectedApp.created_at).toLocaleDateString()}
                </span>
              </DialogDescription>
            )}
          </DialogHeader>

          {selectedApp && (
            <div className="space-y-5">
              <div>
                <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-primary" /> Agency Information
                </h4>
                <div className="border rounded-xl p-4 bg-muted/30">
                  {renderInfoRow(<Building2 className="h-4 w-4" />, 'Agency Name', selectedApp.agency_name)}
                  {renderInfoRow(<User className="h-4 w-4" />, 'Manager Name', selectedApp.manager_name)}
                  {renderInfoRow(<Mail className="h-4 w-4" />, 'Email', selectedApp.email)}
                  {renderInfoRow(<Phone className="h-4 w-4" />, 'Phone', selectedApp.phone)}
                  {renderInfoRow(<MapPin className="h-4 w-4" />, 'Location', [selectedApp.city, selectedApp.address].filter(Boolean).join(', '))}
                  {renderInfoRow(<Globe className="h-4 w-4" />, 'Website', selectedApp.website)}
                </div>
              </div>

              <Separator />

              <div>
                <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" /> Uploaded Documents
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(docLabels).map(([field, label]) => {
                    const hasDoc = !!selectedApp[field];
                    const isImage = field === 'logo_url' || field === 'photo_url';
                    return (
                      <div key={field} className={`flex items-center justify-between p-3 rounded-lg border text-sm ${hasDoc ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-muted/50 border-dashed'}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          {isImage ? <ImageIcon className={`h-4 w-4 ${hasDoc ? 'text-green-600' : 'text-muted-foreground'}`} /> :
                            <FileText className={`h-4 w-4 ${hasDoc ? 'text-green-600' : 'text-muted-foreground'}`} />}
                          <span className={`truncate ${hasDoc ? 'font-medium' : 'text-muted-foreground'}`}>{label}</span>
                          {hasDoc && <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />}
                        </div>
                        {hasDoc && (
                          <div className="flex gap-1 shrink-0">
                            {isImage && (
                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => viewDocImage(selectedApp[field])}>
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => getDocUrl(selectedApp[field])}>
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 text-xs text-muted-foreground">
                  {Object.keys(docLabels).filter(f => !!selectedApp[f]).length} of {Object.keys(docLabels).length} documents uploaded
                </div>
              </div>

              {selectedApp.reviewer_notes && (
                <>
                  <Separator />
                  <div className="p-4 bg-muted rounded-xl">
                    <div className="text-xs font-bold mb-1">Previous Review Notes</div>
                    <p className="text-sm">{selectedApp.reviewer_notes}</p>
                  </div>
                </>
              )}

              {selectedApp.status === 'pending' && (
                <div className="space-y-3 pt-2">
                  <Textarea
                    placeholder="Add review notes — explain the reason if rejecting..."
                    value={reviewNotes}
                    onChange={e => setReviewNotes(e.target.value)}
                    rows={3}
                  />
                  <DialogFooter className="gap-2">
                    <Button
                      variant="destructive"
                      onClick={() => { setActionType('reject'); reviewMutation.mutate({ appId: selectedApp.id, status: 'rejected', notes: reviewNotes }); }}
                      disabled={reviewMutation.isPending}
                    >
                      <XCircle className="h-4 w-4 mr-1" /> Reject
                    </Button>
                    <Button
                      onClick={() => { setActionType('approve'); reviewMutation.mutate({ appId: selectedApp.id, status: 'approved', notes: reviewNotes }); }}
                      disabled={reviewMutation.isPending}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Approve & Create Agency
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Document Preview</DialogTitle></DialogHeader>
          {previewImage && <img src={previewImage} alt="Document preview" className="w-full rounded-lg" />}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!duplicateConfirm} onOpenChange={() => setDuplicateConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Duplicate Agency Name
            </AlertDialogTitle>
            <AlertDialogDescription>
              An agency named <strong>"{duplicateConfirm?.name}"</strong> already exists in the system.
              Approving this application will create another agency with the same name.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (selectedApp) {
                  reviewMutation.mutate({ appId: selectedApp.id, status: 'approved', notes: reviewNotes, force: true });
                }
              }}
            >
              Approve Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
